import { divisionIdParamSchema, roundIdParamSchema, teamIdParamSchema } from '~/schemas';
import { EligibleResultsListTable } from './components/eligible-results-list-table';
import { CurrentResultsListTable } from './components/current-results-list-table';
import { useDivisionDetailQuery } from '~/pages/divisions/queries';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { withAuthentication } from '~/hocs/withAuthentication';
import { useRoundDetailsQuery } from '~/pages/rounds/queries';
import { useCreateRoundResultMutation } from '../mutations';
import { Heading } from '~/components/Heading/Heading';
import { useRoundResultsListQuery } from '../queries';
import { useTeamsQuery } from '~/pages/teams/queries';
import { Button } from '~/components/Button/Button';
import { Panel } from '~/components/Panel/Panel';
import { addDays, endOfDay } from '~/utils/date';
import { useParams } from '~/hooks/useParams';
import { Tabs } from '~/components/Tabs/Tabs';
import { Grid } from '~/components/Grid/Grid';
import { Fragment } from 'react/jsx-runtime';
import { toast } from 'react-toastify';

const Content = withAuthentication(({ user }) => {
  const { divisionId } = useParams(divisionIdParamSchema.parse);
  const { roundId } = useParams(roundIdParamSchema.parse);
  const { teamId } = useParams(teamIdParamSchema.parse);

  const { data: roundResults } = useRoundResultsListQuery({ team: teamId, round: roundId });
  const { data: round } = useRoundDetailsQuery(roundId);
  const { data } = useDivisionDetailQuery(divisionId);
  const { data: team } = useTeamsQuery(teamId);

  const mutation = useCreateRoundResultMutation();

  async function handleCreate() {
    await toast.promise(mutation.mutateAsync({ team: teamId, round: roundId }), {
      success: 'Resultatet har skapats',
      pending: 'Skapar resultat...',
    });
  }

  if (!round || !roundResults || !team || !data) return false;

  const isClubAdmin = !user.permissions.federation_admin;

  const end = endOfDay(addDays(new Date(round.end), 2));
  const now = new Date();

  const deadlinePassed = end < now;

  return (
    <Fragment>
      <PageLayout.Header>
        <PageLayout.Heading
          text={`Hantera omgångsresultat - ${team.name}`}
          subText={`${data.series.name} - ${data.series.year}`}
        />
      </PageLayout.Header>

      <Tabs>
        {data?.rounds.map(round => (
          <Tabs.Tab key={round.id} to={`/series/divisions/${divisionId}/teams/${teamId}/manage/rounds/${round.id}`}>
            {round.name}
          </Tabs.Tab>
        ))}
      </Tabs>

      {isClubAdmin && deadlinePassed && (
        <Panel variant="informative" size="sm" style={{ marginBottom: 'var(--size-4)' }}>
          <p>
            Deadline för att lägga till resultat har passerat. Du kan inte lägga till eller ändra resultat för denna
            omgång.
          </p>
        </Panel>
      )}
      <PageLayout.Section>
        <PageLayout.SubHeading>{String(round.name)}</PageLayout.SubHeading>
        {roundResults.results.length > 0 ? (
          <Grid>
            <Grid.Item cols={{ sm: 12, md: 6 }}>
              <Heading as="h4">Valbara resultat</Heading>
              <EligibleResultsListTable />
            </Grid.Item>
            <Grid.Item cols={{ sm: 12, md: 6 }}>
              <CurrentResultsListTable resultsPerRound={data?.results_per_round ?? 0} />
            </Grid.Item>
          </Grid>
        ) : (
          <Button onClick={handleCreate}>Skapa resultat</Button>
        )}
      </PageLayout.Section>
    </Fragment>
  );
});

export function Page() {
  return <Content />;
}
